import {
  Button,
  Form,
  Modal,
  Row,
  Select,
  Typography,
  Input,
} from "../../common/utils/antd-imports/AntdImports";
import React, { useEffect, useState } from "react";
import "./RejectTerms.css";
import { useDispatch } from "react-redux";
import {
  getSellerTermsRequests,
  updateSellerTermsRequests,
} from "../../../store/thunks/seller-thunks/SellerThunk";

const RejectTerms = ({ rejectModal, setRejectModal, termRequest }) => {
  const dispatch = useDispatch();
  const { Text } = Typography;
  const { TextArea } = Input;
  const { Option } = Select;
  const [form] = Form.useForm();
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherComments, setOtherComments] = useState("");
  const requestDetails =
    termRequest?.terms?.find((item) => item?.request_type) || {};
  const isHoldRequest = requestDetails?.request_type === "hold_request";
  const holdRejectionReasons = [
    {
      label: "Product is no longer available",
      value: "product_unavailable",
      description: "The product cannot be reserved for the requested period.",
    },
    {
      label: "Already reserved or under negotiation",
      value: "already_reserved",
      description:
        "The product is currently reserved or being negotiated with another buyer.",
    },
    {
      label: "Hold request does not meet approval criteria",
      value: "approval_criteria",
      description:
        "The request could not be approved based on our booking policy.",
    },
    {
      label: "Other",
      value: "other",
      description: "Provide a custom reason.",
    },
  ];
  const termsRejectionReasons = [
    {
      label: "Not Acceptable",
      value: "not_acceptable",
      description: "Terms don't match our payment policy",
    },
    {
      label: "Prefer Upfront",
      value: "prefer_upfront",
      description: "Need higher upfront payment",
    },
    {
      label: "Payment Delay",
      value: "payment_delay",
      description: "Terms push payment too far out",
    },
    {
      label: "Net Terms Limit",
      value: "net_terms_limit",
      description: "We don't offer beyond Net 30",
    },
    {
      label: "Mismatch",
      value: "mismatch",
      description: "Terms conflict with internal finance rules",
    },
    {
      label: "Other",
      value: "other",
      description: "Add your reason (optional)",
    },
  ];
  const rejectionReasons = isHoldRequest
    ? holdRejectionReasons
    : termsRejectionReasons;

  const handleSubmitRequest = async () => {
    try {
      const values = await form.validateFields();
      const selectedReasonDetails = rejectionReasons.find(
        (reason) => reason.value === values.rejectionReason,
      );

      const payload = {
        approval_status: 2,
        campaign_id: termRequest?.campaign_id,
        product_id: termRequest?.product_id,
        comment_type:
          selectedReason === "other"
            ? "other"
            : selectedReasonDetails?.description || "",
        other_comments: selectedReason === "other" ? otherComments : "",
        ...(isHoldRequest && {
          product_booking_id: termRequest?.productbookingid,
        }),
      };
      dispatch(updateSellerTermsRequests(payload)).then(() =>
        dispatch(getSellerTermsRequests(0)),
      );

      form.resetFields();
      setSelectedReason("");
      setOtherComments("");
      setRejectModal(false);
    } catch (error) {
      // Validation failed - Form.Item will automatically display the error.
    }
  };

  return (
    <Modal
      title={
        <Row>
          <Text className="reject-terms-he">
            {isHoldRequest ? "Reject Hold Request" : "Reject Terms"}
          </Text>
        </Row>
      }
      open={rejectModal}
      footer={false}
      closeIcon={false}
      destroyOnClose
    >
      <Row>
        <Text className="reject-terms-sub">
          {isHoldRequest
            ? "Share why you're rejecting this hold request"
            : "Share your reason for rejecting these terms"}
        </Text>
      </Row>
      <Row>
        <Text className="reject-terms-text">
          {isHoldRequest
            ? "Help the buyer understand why this hold request was rejected."
            : "Buyers appreciate transparency - help them know what didn’t work."}
        </Text>
      </Row>
      <Row>
        <Text className="reject-terms-reason-he">
          {isHoldRequest
            ? "Reason for rejecting hold request"
            : "Reason for rejection"}
        </Text>
      </Row>

      <Form layout="vertical" form={form}>
        <Form.Item
          name="rejectionReason"
          rules={[
            {
              required: true,
              message: "Please select a reason.",
            },
          ]}
        >
          <div className="reject-reason-select" style={{ width: "100%" }}>
            <Select
              placeholder="Select a reason"
              optionLabelProp="label"
              onChange={(value) => {
                setSelectedReason(value);

                form.setFieldsValue({
                  rejectionReason: value,
                });

                // Clear custom reason when changing away from Other
                if (value !== "other") {
                  setOtherComments("");
                  form.setFieldsValue({
                    customReason: undefined,
                  });
                }
              }}
            >
              {rejectionReasons?.map((reason) => (
                <Option
                  key={reason?.value}
                  value={reason?.value}
                  label={reason?.label}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{reason.label}</div>
                    <div style={{ fontSize: "12px", color: "#888" }}>
                      {reason?.description}
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        </Form.Item>

        {selectedReason === "other" && (
          <Form.Item
            name="customReason"
            label="Please specify"
            rules={[
              {
                required: true,
                message: "Please provide your reason",
              },
              {
                validator: (_, value) => {
                  if (!value || !value.trim()) {
                    return Promise.reject(
                      new Error("Please provide your reason"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              value={otherComments}
              onChange={(e) => {
                const value = e.target.value;
                setOtherComments(value);
                form.setFieldsValue({
                  customReason: value,
                });
              }}
              className="reason-txt-area"
              placeholder={
                isHoldRequest
                  ? "Please explain why the hold request was rejected..."
                  : "E.g. payment schedule doesn't align with our internal policy"
              }
              rows={4}
            />
          </Form.Item>
        )}
      </Form>

      <Row justify="end">
        <div className="terms-actions">
          <Button
            className="reject-btn"
            onClick={() => {
              setRejectModal(false);
              setSelectedReason(""); // Reset on cancel
            }}
          >
            Cancel
          </Button>
          <Button className="terms-accept-btn" onClick={handleSubmitRequest}>
            {isHoldRequest ? "Reject Hold Request" : "Submit & Reject"}
          </Button>
        </div>
      </Row>
    </Modal>
  );
};

export default RejectTerms;
